import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'console_theme';

/**
 * App-wide light/dark theme. The chosen theme is written to
 * <html data-theme="..."> so global CSS variables (see styles.css)
 * resolve to the matching palette, and persisted to localStorage.
 * Default is 'light' to match the existing console pages.
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly themeSubject = new BehaviorSubject<Theme>(this.readStored());
  readonly theme$ = this.themeSubject.asObservable();

  get theme(): Theme {
    return this.themeSubject.value;
  }

  /** Apply the persisted (or default) theme. Call once at app start. */
  init(): void {
    this.setTheme(this.theme);
  }

  toggle(): void {
    this.setTheme(this.theme === 'dark' ? 'light' : 'dark');
  }

  setTheme(theme: Theme): void {
    // Publish the new palette to the DOM BEFORE notifying subscribers. Order is
    // load-bearing: subscribers that rebuild themed content — the mail bodies in
    // email-detail / compose-modal / inline-reply resolve `--surface`, `--text`
    // and `data-theme` with getComputedStyle, because an iframe cannot inherit
    // the host's custom properties — read the document synchronously inside their
    // handler. Emitting first meant they read the palette that was still on
    // <html>, so every rebuild baked the PREVIOUS theme: the body stayed dark on
    // the light theme, then came back as a white slab on the dark one.
    document.documentElement.setAttribute('data-theme', theme);
    this.themeSubject.next(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // storage may be unavailable (private mode) — theme still applies for the session
    }
  }

  private readStored(): Theme {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'dark' || stored === 'light') {
        return stored;
      }
    } catch {
      // ignore
    }
    return 'light';
  }
}
